/*
  "Wish for its return" for sold-out shades.
  - <arcana-wish-button> on sold-out cards / product pages opens the shared dialog.
  - Any form[data-arcana-wish-form] (dialog + product-page card) is submitted in place when
    possible; if Shopify asks for a captcha or anything unexpected happens we fall back to a
    normal form post, and finish the wish after the page reloads.
  - Wishes (product info only, never the email) are remembered on this device for the "My restock wishes" page;
    the email + Shopify customer tags are what the owners use to email restocks.
  - When the STOQ setting is on, the same email is also sent to STOQ's sign-up endpoint (the one
    its storefront SDK posts to), and STOQ emails the shopper automatically on restock. The
    Shopify customer tags stay as a backup list.
*/
(() => {
  if (customElements.get('arcana-wish-dialog')) return;

  const WISHES = 'arcana-restock-wishes';
  const PENDING = 'arcana-wish-pending';
  const PENDING_MAX_AGE = 30 * 60 * 1000;

  const store = {
    read(key, fallback) {
      try {
        const v = JSON.parse(window.localStorage.getItem(key));
        return v ?? fallback;
      } catch (e) {
        return fallback;
      }
    },
    write(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {}
    },
    remove(key) {
      try {
        window.localStorage.removeItem(key);
      } catch (e) {}
    },
  };

  const wishes = {
    all: () => store.read(WISHES, []),
    has: (handle) => wishes.all().some((w) => w.handle === handle),
    add(product) {
      const list = wishes.all().filter((w) => w.handle !== product.handle);
      list.unshift({ ...product, at: Date.now() });
      store.write(WISHES, list.slice(0, 60));
      document.dispatchEvent(new CustomEvent('arcana:wishes-changed'));
    },
    remove(handle) {
      store.write(WISHES, wishes.all().filter((w) => w.handle !== handle));
      document.dispatchEvent(new CustomEvent('arcana:wishes-changed'));
    },
  };
  window.ArcanaWishes = wishes;

  const tagsFor = (p) =>
    ['newsletter', 'back-in-stock', `bis-${p.handle}`].concat(p.multi ? [`bis-variant-${p.variant}`] : []).join(',');

  const productFrom = (el) => ({
    handle: el.dataset.handle,
    title: el.dataset.title,
    url: el.dataset.url,
    image: el.dataset.image || '',
    productId: el.dataset.productId,
    variant: el.dataset.variant,
    variantCount: Number(el.dataset.variantCount) || 1,
    multi: el.dataset.multi === 'true',
  });

  // Submit a Shopify customer form without leaving the page. Resolves true on success,
  // false when the caller should fall back to a normal submit.
  async function submitInPlace(form) {
    try {
      const res = await fetch(form.action, {
        method: 'POST',
        // Same encoding as a normal browser form post.
        body: new URLSearchParams(new FormData(form)),
        headers: { Accept: 'text/html', 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'same-origin',
      });
      const ok = res.ok && res.url.includes('customer_posted=true');
      // We only need the redirect URL, not the ~300KB page behind it.
      res.body?.cancel().catch(() => {});
      return ok;
    } catch (e) {
      return false;
    }
  }

  // Sign the shopper up for STOQ's automatic restock email (same payload as STOQ's storefront
  // SDK createSignup with an email only). Never throws; resolves true when STOQ created it (201).
  // Marketing consent stays with the Shopify form, so no accepts_marketing here.
  const STOQ_ENDPOINT = 'https://app.stoqapp.com/api/v1/intents.json';
  async function signUpWithStoq(host, product, email) {
    const shop = host.dataset.stoqShop;
    if (!shop || !product?.productId || !product.variant) return false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3000);
    try {
      const res = await fetch(STOQ_ENDPOINT, {
        method: 'POST',
        headers: { 'X-Shopify-Shop-Domain': shop, 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          intent: {
            shopify_variant_id: Number(product.variant),
            shopify_product_id: Number(product.productId),
            quantity: 1,
            channel: 'email',
            source: 'storefront-sdk',
          },
          customer: { email, locale: host.dataset.locale || undefined },
          product: { title: product.title, variant_count: product.variantCount },
        }),
        signal: controller.signal,
      });
      if (res.status === 201) return true;
      // 422 is usually "already signed up"; anything else is worth seeing in the console.
      console.warn('[Arcana] STOQ sign-up not accepted', res.status, await res.text().catch(() => ''));
      return false;
    } catch (e) {
      console.warn('[Arcana] STOQ sign-up failed', e);
      return false;
    } finally {
      clearTimeout(timer);
    }
  }

  class ArcanaWishButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.onClick = () => document.querySelector('arcana-wish-dialog')?.open(productFrom(this), this.button);
      this.button?.addEventListener('click', this.onClick);
      this.onChange = () => this.render();
      document.addEventListener('arcana:wishes-changed', this.onChange);
      this.render();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener('arcana:wishes-changed', this.onChange);
    }

    render() {
      const wished = wishes.has(this.dataset.handle);
      this.classList.toggle('is-wished', wished);
      this.querySelector('[data-default]').hidden = wished;
      this.querySelector('[data-wished]').hidden = !wished;
    }
  }

  class ArcanaWishDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form[data-arcana-wish-form]');
      this.toast = this.querySelector('[data-wish-toast]');
      this.querySelectorAll('[data-wish-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close(); // backdrop click
      });
      this.dialog.addEventListener('close', () => this.trigger?.focus());

      // One delegated handler for every wish form (dialog + product-page card).
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[data-arcana-wish-form]');
        if (form) this.handleSubmit(e, form);
      });

      this.resumeAfterReload();
    }

    open(product, trigger) {
      this.product = product;
      this.trigger = trigger;
      this.querySelector('[data-wish-title]').textContent = product.title;
      const img = this.querySelector('[data-wish-image]');
      img.hidden = !product.image;
      if (product.image) img.src = product.image;
      this.querySelector('[data-wish-tags]').value = tagsFor(product);
      const email = this.form.querySelector('input[type="email"]');
      this.setStatus('');
      this.showStep('form');
      this.dialog.showModal();
      email.focus();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    showStep(step) {
      this.querySelectorAll('[data-wish-step]').forEach((el) => (el.hidden = el.dataset.wishStep !== step));
      if (step === 'done') {
        const link = this.querySelector('[data-wish-page-link]');
        if (this.dataset.wishesUrl) {
          link.href = this.dataset.wishesUrl;
          link.hidden = false;
        }
        this.querySelector('[data-wish-success]').focus();
      }
    }

    setStatus(msg) {
      const s = this.querySelector('#ArcanaWishStatus');
      if (s) s.textContent = msg;
    }

    // The product a form is for: the dialog's current product, or the product-page card's data.
    productForForm(form) {
      if (form === this.form) return this.product;
      const host = form.closest('[data-handle]');
      return host ? productFrom(host) : null;
    }

    async handleSubmit(event, form) {
      const product = this.productForForm(form);
      if (!product) return; // let it submit normally
      event.preventDefault();
      const email = form.querySelector('input[type="email"]');
      if (!email.checkValidity()) {
        email.reportValidity();
        return;
      }
      if (form.dataset.submitting === 'true') return; // ignore double taps
      form.dataset.submitting = 'true';
      const submit = form.querySelector('[type="submit"]');
      submit?.setAttribute('aria-disabled', 'true');
      const cardStatus = form.querySelector('[data-wish-card-status]');
      if (form === this.form) this.setStatus(this.dataset.msgSending);
      if (cardStatus) cardStatus.textContent = this.dataset.msgSending;

      store.write(PENDING, { product, formId: form.id, at: Date.now() });

      // STOQ first-class, Shopify tags as the backup list: send both at once.
      const [ok] = await Promise.all([submitInPlace(form), signUpWithStoq(this, product, email.value.trim())]);
      submit?.removeAttribute('aria-disabled');
      form.dataset.submitting = 'false';
      if (cardStatus) cardStatus.textContent = '';
      if (!ok) {
        // Captcha, validation error or network trouble: let Shopify handle it the normal way.
        HTMLFormElement.prototype.submit.call(form);
        return;
      }
      store.remove(PENDING);
      wishes.add(product);
      if (form === this.form) {
        this.setStatus('');
        this.showStep('done');
      } else {
        form.closest('[data-wish-card]')?.classList.add('is-wished');
        const done = form.closest('[data-wish-card]')?.querySelector('[data-wish-card-done]');
        if (done) {
          done.hidden = false;
          form.hidden = true;
          done.focus();
        }
      }
    }

    // After a normal (fallback) form post the page reloads: finish the wish, or reopen on errors.
    resumeAfterReload() {
      const pending = store.read(PENDING, null);
      if (!pending) return;
      if (!pending.at || Date.now() - pending.at > PENDING_MAX_AGE) {
        store.remove(PENDING); // abandoned (e.g. captcha closed); never attach it to another form
        return;
      }
      // Only count a success that came from *this* form (Shopify appends the form id as the hash).
      const posted =
        window.location.search.includes('customer_posted=true') &&
        (!window.location.hash || window.location.hash === `#${pending.formId}`);
      const form = pending.formId ? document.getElementById(pending.formId) : null;
      if (posted) {
        store.remove(PENDING);
        wishes.add(pending.product);
        this.showToast(this.querySelector('[data-wish-success]').textContent.trim());
      } else if (form && form === this.form && this.querySelector('[data-wish-errors]')) {
        this.open(pending.product, null);
        this.setStatus(this.dataset.msgError);
      }
    }

    showToast(text) {
      if (!this.toast) return;
      this.toast.textContent = text;
      this.toast.hidden = false;
      setTimeout(() => (this.toast.hidden = true), 6000);
    }
  }

  customElements.define('arcana-wish-button', ArcanaWishButton);
  customElements.define('arcana-wish-dialog', ArcanaWishDialog);
})();
